import { AtticErrorCode } from "@dashboard/graphql";

/**
 * The types the bucket accepts. The backend sniffs the bytes on confirm and has
 * the last word; this list only spares the merchant a round trip for a file it
 * would refuse anyway.
 */
export const ATTIC_ASSET_ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/svg+xml",
  "application/pdf",
  "video/mp4",
  "font/woff2",
  "font/woff",
  "image/x-icon",
] as const;

/**
 * What a browser that reports no type (or a legacy one) for a file means by its
 * extension. Fonts and icons are the usual cases: some systems send "" for
 * .woff2 and "image/vnd.microsoft.icon" for .ico.
 */
const TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  avif: "image/avif",
  svg: "image/svg+xml",
  pdf: "application/pdf",
  mp4: "video/mp4",
  woff2: "font/woff2",
  woff: "font/woff",
  ico: "image/x-icon",
};

const TYPE_ALIASES: Record<string, string> = {
  "image/jpg": "image/jpeg",
  "image/vnd.microsoft.icon": "image/x-icon",
  "application/font-woff": "font/woff",
  "application/x-font-woff": "font/woff",
  "application/font-woff2": "font/woff2",
};

/** The accepted content type for a file, or null when it is not one the bucket takes. */
export const getAtticAssetContentType = (file: Pick<File, "name" | "type">): string | null => {
  const reported = (file.type || "").toLowerCase();
  const normalised = TYPE_ALIASES[reported] ?? reported;

  if ((ATTIC_ASSET_ACCEPTED_TYPES as readonly string[]).includes(normalised)) {
    return normalised;
  }

  if (!reported || reported === "application/octet-stream") {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

    return TYPE_BY_EXTENSION[extension] ?? null;
  }

  return null;
};

export type AtticAssetFileRefusal =
  | { reason: "type" }
  | { reason: "empty" }
  | { reason: "tooLarge"; maxBytes: number }
  | { reason: "folder" };

/** Why the browser will not even try this file, or null when it may go. */
export const validateAtticAssetFile = (
  file: Pick<File, "name" | "type" | "size">,
  maxUploadBytes: number,
): AtticAssetFileRefusal | null => {
  if (!getAtticAssetContentType(file)) {
    return { reason: "type" };
  }

  if (file.size <= 0) {
    return { reason: "empty" };
  }

  if (file.size > maxUploadBytes) {
    return { reason: "tooLarge", maxBytes: maxUploadBytes };
  }

  return null;
};

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

/**
 * Bytes for a merchant: 1024-based, one decimal under 10 of a unit and none
 * above, so a column of sizes stays short. 0 is "0 B". The number is written
 * the locale's way (`intl.locale`: "1,5 MB" in German, "2,048 TB" in English).
 */
export const formatBytes = (bytes: number | null | undefined, locale?: string): string => {
  const format = (value: number, fractionDigits: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: fractionDigits }).format(value);

  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes <= 0) {
    return `${format(0, 0)} B`;
  }

  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;

  if (exponent === 0) {
    return `${format(Math.round(value), 0)} B`;
  }

  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);

  return `${format(rounded, 1)} ${BYTE_UNITS[exponent]}`;
};

/** Share of the advisory limit in use, 0-100, for the usage line. */
export const getUsagePercent = (byteCount: number, byteLimit: number): number => {
  if (!byteLimit || byteLimit <= 0) {
    return 0;
  }

  return Math.min(100, Math.round((byteCount / byteLimit) * 100));
};

/**
 * The headers the signed PUT must carry, exactly as the backend returned them:
 * every one is signed into the URL, so none may be dropped, renamed or
 * re-cased, and nothing (no Authorization) may be added.
 */
export const getUploadHeaders = (
  headers: ReadonlyArray<{ name: string; value: string }> | null | undefined,
): Record<string, string> =>
  (headers ?? []).reduce<Record<string, string>>((acc, header) => {
    acc[header.name] = header.value;

    return acc;
  }, {});

/**
 * Folders inside the directory, as the merchant typed them: trimmed, with the
 * slashes at either end and doubled slashes dropped. Empty means the
 * directory's root. The backend cleans and checks it again.
 */
export const normaliseSubpath = (subpath: string): string =>
  subpath
    .split("/")
    .map(part => part.trim())
    .filter(Boolean)
    .join("/");

// The backend's folder segment (attic/assets/service.py `_SEGMENT_RE`): a letter
// or digit, then letters, digits, ".", "_" or "-", 128 at most.
const SUBPATH_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

/** Whether the backend would take this (already normalised) subpath. Empty is the root. */
export const isValidSubpath = (subpath: string): boolean =>
  !subpath || subpath.split("/").every(segment => SUBPATH_SEGMENT.test(segment));

const SEGMENT_MAX_LENGTH = 128;

/**
 * A dropped folder's name as a folder segment the backend takes, cleaned the
 * way attic/assets/service.py `_clean_filename` cleans a file name (whitespace
 * to hyphens, anything else outside letters, digits, ".", "_" and "-"
 * dropped) but keeping its case, which a segment may carry. Leading dots,
 * hyphens and underscores go (a segment starts with a letter or digit) and it
 * is cut to the segment length. Empty when nothing usable is left. A folder
 * the merchant TYPES is never rewritten: it is validated and refused instead.
 */
export const cleanAtticAssetFolderName = (name: string): string =>
  name
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9._-]/g, "")
    .replace(/^[^A-Za-z0-9]+/, "")
    .slice(0, SEGMENT_MAX_LENGTH);

/**
 * The file name the backend will store (attic/assets/service.py
 * `_clean_filename`: lower-cased, whitespace to hyphens, anything outside
 * letters, digits, ".", "_" and "-" dropped), for showing the resulting path
 * on the row. The upload still sends the name as dropped; the backend cleans it.
 */
export const cleanAtticAssetFilename = (filename: string): string =>
  filename
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._-]/g, "");

/** Where a file lands inside its directory, as the backend will name it, for its upload row. */
export const getAtticAssetUploadPath = ({
  filename,
  subpath,
}: {
  filename: string;
  subpath: string;
}) => {
  const name = cleanAtticAssetFilename(filename) || filename;

  return subpath ? `${subpath}/${name}` : name;
};

/**
 * The key a file would get inside its directory, built the way
 * attic/assets/service.py `build_key` builds it: the (already normalised)
 * subpath as typed, case kept, then the cleaned, lower-cased file name. Two
 * uploads with the same value here race for one object, so the queue lets
 * only one of them run. Null when the backend would refuse the name or the
 * folder anyway (that refusal is the row's message, not a collision).
 */
export const getAtticAssetUploadKey = (subpath: string, filename: string): string | null => {
  const name = cleanAtticAssetFilename(filename);

  if (!SUBPATH_SEGMENT.test(name) || !isValidSubpath(subpath)) {
    return null;
  }

  return subpath ? `${subpath}/${name}` : name;
};

// What an OS leaves in a folder that nobody means to upload.
const IGNORED_SEGMENTS = new Set(["__MACOSX", "Thumbs.db", "desktop.ini"]);

/** A dropped entry to skip without a row: a dotfile, a file in a dot-folder, OS litter. */
export const isIgnoredUploadPath = (path: string): boolean =>
  path
    .split("/")
    .filter(Boolean)
    .some(segment => segment.startsWith(".") || IGNORED_SEGMENTS.has(segment));

/** A dropped file: react-dropzone (file-selector) sets `path`, "/folder/a.png" for a folder drop. */
export type AtticAssetDroppedFile = File & { path?: string };

export interface AtticAssetUploadEntry {
  file: File;
  /** Normalised; a typed part may still be invalid, which the upload refuses on its row. */
  subpath: string;
  /**
   * The dropped folder (as named on disk) whose name has nothing the backend
   * takes in a folder name. The file is refused on its row rather than put
   * one level up, where it could land on a name the merchant never chose.
   */
  unusableFolder?: string;
}

export interface AtticAssetUploadPlan {
  entries: AtticAssetUploadEntry[];
  /** Dotfiles and OS litter skipped, so a drop of nothing else can say so. */
  ignored: number;
}

/**
 * The uploads a drop or a pick means. A dropped folder keeps its shape: each
 * file goes under the typed subpath plus the folders it was in (their names
 * cleaned into segments the backend takes), so same-named files in different
 * subfolders do not collide on one key. A folder whose name cleans to nothing
 * marks its files as refused (`unusableFolder`). Dotfiles and OS litter are
 * skipped and counted; a real drag never even delivers `.DS_Store` or
 * `Thumbs.db` (react-dropzone's file-selector drops them first).
 */
export const planAtticAssetUploads = (
  files: AtticAssetDroppedFile[],
  typedSubpath: string,
): AtticAssetUploadPlan => {
  const base = normaliseSubpath(typedSubpath);
  const kept = files.filter(file => !isIgnoredUploadPath(file.path || file.name));

  return {
    ignored: files.length - kept.length,
    entries: kept.map(file => {
      const dropped = (file.path || "").split("/").filter(Boolean).slice(0, -1);
      const folders = dropped.map(cleanAtticAssetFolderName);
      const unusable = dropped.find((_, index) => !folders[index]);
      const subpath = normaliseSubpath([base, ...folders.filter(Boolean)].join("/"));

      return unusable === undefined
        ? { file, subpath }
        : { file, subpath, unusableFolder: unusable };
    }),
  };
};

/**
 * Why a directory cannot be deleted, read from its own structured fields only
 * (never from the backend's `removalRefusal` sentence), in the order
 * attic/assets/models.py `removal_refusal` checks them: a default directory,
 * one an active app holds, one with live files. `isProtected` alone does not
 * refuse a delete there, so it is not read here. "busy" when no field explains
 * the refusal (a pending upload in it, today).
 */
export type AtticAssetDirectoryRemovalRefusal = "default" | "managed" | "notEmpty" | "busy";

/** Null when the directory may be deleted. */
export const getDirectoryRemovalRefusal = (directory: {
  removable: boolean;
  isDefault: boolean;
  claimedBy?: string | null;
  objectCount: number;
}): AtticAssetDirectoryRemovalRefusal | null => {
  if (directory.removable) {
    return null;
  }

  if (directory.isDefault) {
    return "default";
  }

  if (directory.claimedBy) {
    return "managed";
  }

  if (directory.objectCount > 0) {
    return "notEmpty";
  }

  return "busy";
};

/** True when an app holds the directory, so staff may read it and copy URLs only. */
export const isManagedByApp = (directory: { isProtected: boolean; claimedBy?: string | null }) =>
  directory.isProtected || !!directory.claimedBy;

// Originals small enough to stand in for a thumbnail when the backend renders
// none: SVG and ICO are usually a few KB. A GIF original never is a thumbnail
// (it may be 25 MB and animated), nor is anything over this size.
const ORIGINAL_AS_THUMBNAIL_TYPES = ["image/svg+xml", "image/x-icon"];
const ORIGINAL_AS_THUMBNAIL_MAX_BYTES = 256 * 1024;

/**
 * The waits before each new try at a thumbnail that failed to load. The
 * backend answers 503 (with Retry-After) while its render slots are busy, so
 * a first failure is often only "not yet"; after the last try the row keeps
 * its placeholder (a 404 never comes right). An <img> cannot read
 * Retry-After, hence fixed waits.
 */
export const THUMBNAIL_RETRY_DELAYS_MS = [2_000, 5_000, 10_000];

/**
 * The picture a list row shows: the backend's rendition for a JPEG, PNG,
 * WebP or AVIF; the original only for a small SVG or ICO; otherwise none
 * (the row shows a file-type placeholder).
 */
export const getThumbnailSrc = (asset: {
  contentType: string;
  size: number;
  url: string | null;
  thumbnailUrl: string | null;
}): string | null => {
  if (asset.thumbnailUrl) {
    return asset.thumbnailUrl;
  }

  if (
    asset.url &&
    ORIGINAL_AS_THUMBNAIL_TYPES.includes(asset.contentType) &&
    asset.size <= ORIGINAL_AS_THUMBNAIL_MAX_BYTES
  ) {
    return asset.url;
  }

  return null;
};

export type AtticAssetDirectoryErrorTarget = "name" | "slug" | "form";

/**
 * Where the directory dialog shows a refusal. The backend sends every one
 * with `field: null` (a plain ValidationError), so the code decides: on
 * create, a taken, reserved or invalid folder belongs to the folder field
 * when the merchant typed one and to the name it was made from when not; on
 * rename, only an invalid name is the name's. Anything else is the form's.
 */
export const getDirectoryErrorTarget = (
  code: AtticErrorCode,
  mode: "create" | "rename",
  slugTyped: boolean,
): AtticAssetDirectoryErrorTarget => {
  if (mode === "rename") {
    return code === AtticErrorCode.ASSET_KEY_INVALID ? "name" : "form";
  }

  switch (code) {
    case AtticErrorCode.ASSET_KEY_TAKEN:
    case AtticErrorCode.ASSET_DIRECTORY_RESERVED:
    case AtticErrorCode.ASSET_KEY_INVALID:
      return slugTyped ? "slug" : "name";
    default:
      return "form";
  }
};

export interface AtticAssetRefreshThrottle {
  /** Something changed: refresh now if the last one is old enough, else when the interval is up. */
  request: () => void;
  /** The queue is over: refresh now if one is owed, and drop the pending timer. */
  flush: () => void;
  /** Leaving: drop the pending timer, refresh nothing. */
  cancel: () => void;
}

/**
 * At most one `refresh` per `intervalMs` while requests keep coming, the
 * first at once and the rest on a trailing timer, plus one on `flush` if any
 * request is still owed.
 */
export const createRefreshThrottle = (
  refresh: () => void,
  intervalMs: number,
): AtticAssetRefreshThrottle => {
  let last = Number.NEGATIVE_INFINITY;
  let due = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };
  const fire = () => {
    cancel();

    if (!due) {
      return;
    }

    due = false;
    last = Date.now();
    refresh();
  };

  return {
    request: () => {
      due = true;

      if (timer !== undefined) {
        return;
      }

      const wait = last + intervalMs - Date.now();

      if (wait <= 0) {
        fire();
      } else {
        timer = setTimeout(fire, wait);
      }
    },
    flush: fire,
    cancel,
  };
};
